import { Connection } from "~/stores/slices/database";
import {
  FaDatabase,
  FaCheckCircle,
  FaTimesCircle,
  FaEllipsisV,
} from "react-icons/fa";
import {
  SiPostgresql,
  SiMysql,
  SiSnowflake,
  SiShopify,
  SiMetabase,
  SiInstagram,
} from "react-icons/si";

type ConnectionCardProps = {
  connection: Connection;
  handleEditConnection: (connection: Connection) => void;
  confirmDelete: (connection: Connection) => void;
};

const ConnectionCard = ({
  connection,
  handleEditConnection,
  confirmDelete,
}: ConnectionCardProps) => {
  const getConnectionIcon = (type: string) => {
    const iconClass = "w-5 h-5";
    switch (type.toLowerCase()) {
      case "postgresql":
        return <SiPostgresql className={`${iconClass} text-blue-600`} />;
      case "mysql":
        return <SiMysql className={`${iconClass} text-orange-600`} />;
      case "snowflake":
        return <SiSnowflake className={`${iconClass} text-cyan-600`} />;
      case "shopify":
        return <SiShopify className={`${iconClass} text-green-600`} />;
      case "metabase":
        return <SiMetabase className={`${iconClass} text-purple-600`} />;
      case "instagram":
        return <SiInstagram className={`${iconClass} text-pink-600`} />;
      default:
        return <FaDatabase className={`${iconClass} text-base-content/60`} />;
    }
  };

  const getConnectionStatus = () => {
    // Check sync_status for Shopify connections
    if (connection.connection_type === "shopify" && connection.sync_status) {
      if (connection.sync_status === "COMPLETED") {
        return {
          badge: "badge-success",
          text: "Connected",
        };
      } else if (connection.sync_status === "FAILED") {
        return {
          badge: "badge-error",
          text: "Failed",
        };
      } else if (
        connection.sync_status === "IN_SYNC" ||
        connection.sync_status === "PENDING"
      ) {
        return {
          badge: "badge-warning",
          text: "Syncing",
        };
      }
    }

    // For other connections, use a simple connected status
    return {
      badge: "badge-success",
      text: "Connected",
    };
  };

  const getConnectionColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "postgresql":
        return "bg-blue-500/10";
      case "mysql":
        return "bg-orange-500/10";
      case "snowflake":
        return "bg-cyan-500/10";
      case "shopify":
        return "bg-green-500/10";
      case "metabase":
        return "bg-purple-500/10";
      default:
        return "bg-base-200";
    }
  };

  const status = getConnectionStatus();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="card bg-base-100 shadow hover:shadow-lg transition-all duration-200 border border-base-200">
      <div className="card-body p-5">
        {/* Header with Icon and Title */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div
              className={`p-2.5 rounded-lg ${getConnectionColor(
                connection.connection_type
              )}`}
            >
              {getConnectionIcon(connection.connection_type)}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-base line-clamp-1">
                {connection.name}
              </h3>
              <p className="text-xs text-base-content/60 capitalize mt-0.5">
                {connection.connection_type.replace("_", " ")}
              </p>
            </div>
          </div>

          {/* Dropdown Menu */}
          <div className="dropdown dropdown-end">
            <label tabIndex={0} className="btn btn-ghost btn-xs btn-square">
              <FaEllipsisV className="w-3 h-3" />
            </label>
            <ul
              tabIndex={0}
              className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-40 border border-base-200 z-10"
            >
              {(connection.connection_type.toLowerCase() === "postgresql" ||
                connection.connection_type.toLowerCase() === "mysql") && (
                <li>
                  <button
                    onClick={() => handleEditConnection(connection)}
                    className="text-sm"
                  >
                    Edit Settings
                  </button>
                </li>
              )}
              <li>
                <button
                  onClick={() => confirmDelete(connection)}
                  className="text-sm text-error hover:bg-error/10"
                >
                  Delete
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-3">
          <div className={`badge badge-sm ${status.badge} gap-1`}>
            {status.text === "Syncing" ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : status.text === "Connected" ? (
              <FaCheckCircle className="w-3 h-3" />
            ) : status.text === "Failed" ? (
              <FaTimesCircle className="w-3 h-3" />
            ) : null}
            {status.text}
          </div>
        </div>

        {/* Description if available */}
        {connection.description && (
          <p className="text-sm text-base-content/60 mt-2 line-clamp-2">
            {connection.description}
          </p>
        )}

        {/* Footer with Last Updated */}
        <div className="mt-3 pt-3 border-t border-base-200">
          <p className="text-xs text-base-content/50">
            Last updated: {formatDate(connection.updated_at)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConnectionCard;
